import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Link, useLocation } from 'react-router-dom';
import { canAccessPage, firstAllowedSidebarPath, isAdminUser } from '../../constants/adminPages';
import { ordersApi } from '../../services/subscriberApi';

const DROPDOWN_LINE = 'rgba(255,255,255,0.18)';
const ORDERS_POLL_MS = 30000;

const Icon = ({ path, className = 'h-6 w-6' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
);

const CountBadge = ({ count, blink = false }) => {
  const n = Number(count) || 0;
  if (n <= 0) return null;
  return (
    <span
      className={`ml-auto inline-flex min-w-[1.35rem] h-5 items-center justify-center rounded-full px-1.5 text-[10px] font-black text-white shadow ${
        blink ? 'flare-orders-blink' : ''
      }`}
      style={{ backgroundColor: '#c4151c' }}
      title={`${n} new website order${n === 1 ? '' : 's'}`}
    >
      {n > 99 ? '99+' : n}
    </span>
  );
};

const SidebarLink = ({ to, iconPath, text, badge = 0, blinkBadge = false }) => {
  const location = useLocation();
  const isActive = location.pathname.endsWith(to);

  const linkClasses = `w-full text-left flex items-center gap-4 rounded-md transition-colors duration-200 relative px-4 py-2 text-sm ${
    isActive
      ? 'bg-[#c4151c] text-white font-semibold'
      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
  }`;

  return (
    <Link to={to} className={linkClasses}>
      <Icon path={iconPath} className="h-5 w-5 shrink-0" />
      <span className="truncate">{text}</span>
      <CountBadge count={isActive ? 0 : badge} blink={blinkBadge && !isActive} />
    </Link>
  );
};

const DropdownMenu = ({ title, iconPath, links, defaultOpen = false, headerBadge = 0 }) => {
  const location = useLocation();
  const isAnyLinkActive = links.some((link) => location.pathname.endsWith(link.to));
  const [isOpen, setIsOpen] = useState(isAnyLinkActive || defaultOpen);

  const dropdownHeaderClasses = `w-full text-left px-4 py-3 flex items-center gap-4 rounded-lg transition-colors duration-200 relative cursor-pointer ${
    isAnyLinkActive ? 'text-white' : 'text-gray-300'
  } hover:bg-gray-700 hover:text-white`;

  return (
    <div>
      <div onClick={() => setIsOpen(!isOpen)} className={dropdownHeaderClasses}>
        <Icon path={iconPath} />
        <span className="truncate">{title}</span>
        <CountBadge count={!isOpen ? headerBadge : 0} blink={!isOpen && headerBadge > 0} />
        <svg
          className={`h-5 w-5 shrink-0 transform transition-transform ${isOpen ? 'rotate-90' : ''} ${headerBadge > 0 && !isOpen ? '' : 'ml-auto'}`}
          style={headerBadge > 0 && !isOpen ? { marginLeft: 4 } : undefined}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path d="M9 5l7 7-7 7" />
        </svg>
      </div>
      {isOpen && (
        <div className="mt-2 ml-4 pl-4 border-l-2 space-y-2" style={{ borderLeftColor: DROPDOWN_LINE }}>
          {links.map((link) => (
            <SidebarLink
              key={link.to}
              to={link.to}
              iconPath={link.iconPath}
              text={link.text}
              badge={link.badge || 0}
              blinkBadge={!!link.blinkBadge}
            />
          ))}
        </div>
      )}
    </div>
  );
};

function Sidebar({ isSidebarOpen, setIsSidebarOpen, mainAppLogo, appTagline, user, onLogout, hasAccess }) {
  const pageAllowed = (to) => {
    if (typeof hasAccess === 'function') return hasAccess(to);
    return canAccessPage(user, to);
  };

  const handleLogout = () => {
    if (onLogout) {
      Promise.resolve(onLogout()).catch((err) => toast.error(err?.message || 'Logout failed'));
    }
  };

  const canSeeOrders = pageAllowed('/magazine/orders');
  const [newWebsiteOrders, setNewWebsiteOrders] = useState(0);

  useEffect(() => {
    if (!canSeeOrders) {
      setNewWebsiteOrders(0);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await ordersApi.deskSummary();
        if (cancelled) return;
        // New checkouts waiting payment verify (Website Orders flash).
        const count = Number(data?.pending_payment ?? data?.needs_action ?? 0) || 0;
        setNewWebsiteOrders(count);
      } catch {
        // ignore transient errors in sidebar badge
      }
    };

    load();
    const timer = window.setInterval(load, ORDERS_POLL_MS);
    const onFocus = () => load();
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [canSeeOrders]);

  const salesLinks = [
    { to: '/clients', iconPath: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m-1 4h1m-1 4h1m6-12h1m-1 4h1m-1 4h1m-1 4h1', text: 'Ad Clients' },
    { to: '/sales', iconPath: 'M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', text: 'New Sale / Order' },
    { to: '/invoices', iconPath: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', text: 'Invoices' },
    { to: '/payments', iconPath: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z', text: 'Payments' },
    { to: '/expenses', iconPath: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z', text: 'Expenses' },
    { to: '/billManagement', iconPath: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', text: 'Bill Management' },
  ];

  const magazineLinks = [
    { to: '/magazine/issues', iconPath: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253', text: 'Manage Issues' },
    { to: '/magazine/plans', iconPath: 'M5 5a2 2 0 012-2h10a2 2 0 012 2v1h2a1 1 0 011 1v3a1 1 0 01-1 1h-2v1a2 2 0 01-2 2H7a2 2 0 01-2-2v-1H3a1 1 0 01-1-1V7a1 1 0 011-1h2V5z', text: 'Subscription Plans' },
    { to: '/magazine/subscribers', iconPath: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z', text: 'Subscribers' },
    { to: '/magazine/packing', iconPath: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4', text: 'Packing Desk' },
    { to: '/magazine/newsletter', iconPath: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', text: 'Flash Newsletter' },
    {
      to: '/magazine/orders',
      iconPath: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
      text: 'Website Orders',
      badge: newWebsiteOrders,
      blinkBadge: newWebsiteOrders > 0,
    },
    { to: '/magazine/delivery', iconPath: 'M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm7-9a1 1 0 11-2 0 1 1 0 012 0z', text: 'Delivery Reports' },
    { to: '/magazine/contact-requests', iconPath: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', text: 'Contact Requests' },
    { to: '/magazine/users', iconPath: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z', text: 'User Management' },
  ];

  const hrLinks = [
    { to: '/hr/employees', iconPath: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z', text: 'Employees' },
    { to: '/hr/salaries', iconPath: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z', text: 'Salaries' },
  ];

  const attendanceLinks = [
    { to: '/attendance/kiosk', iconPath: 'M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z', text: 'Live Kiosk' },
    { to: '/attendance/report', iconPath: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', text: 'Reports' },
  ];

  const emailCenterLinks = [
    {
      to: '/admin/email-center',
      iconPath: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
      text: 'Notifications & Templates',
    },
    {
      to: '/admin/email-logs',
      iconPath: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
      text: 'Send History',
    },
  ];

  const adminLinks = [
    { to: '/admin/users', iconPath: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M15 21v-2a6 6 0 00-12 0v2', text: 'User Management' },
    { to: '/admin/settings', iconPath: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924-1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z', text: 'App Settings' },
    { to: '/admin/plugins', iconPath: 'M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z', text: 'Plugins' },
    { to: '/admin/seo', iconPath: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z', text: 'SEO' },
  ];

  const location = useLocation();
  const visibleSales = salesLinks.filter((link) => pageAllowed(link.to));
  const visibleMagazine = magazineLinks.filter((link) => pageAllowed(link.to));
  const visibleHr = hrLinks.filter((link) => pageAllowed(link.to));
  const visibleAttendance = attendanceLinks.filter((link) => pageAllowed(link.to));
  const visibleEmailCenter = isAdminUser(user) ? emailCenterLinks : [];
  const showDashboard = pageAllowed('/dashboard');
  const homeTo = firstAllowedSidebarPath(user);
  const onOrdersPage = location.pathname.endsWith('/magazine/orders');

  return (
    <>
      <style>{`
        @keyframes flare-orders-blink {
          0%, 100% { opacity: 1; transform: scale(1); box-shadow: 0 0 0 0 rgba(196,21,28,0.55); }
          50% { opacity: 0.55; transform: scale(1.08); box-shadow: 0 0 0 6px rgba(196,21,28,0); }
        }
        .flare-orders-blink {
          animation: flare-orders-blink 1.1s ease-in-out infinite;
        }
      `}</style>
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-[#c4151c] text-white rounded-lg shadow-md"
        aria-label="Toggle sidebar"
      >
        <Icon path={isSidebarOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'} />
        {!isSidebarOpen && newWebsiteOrders > 0 ? (
          <span className="absolute -top-1 -right-1 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-white text-[#c4151c] text-[9px] font-black flex items-center justify-center flare-orders-blink">
            {newWebsiteOrders > 99 ? '99+' : newWebsiteOrders}
          </span>
        ) : null}
      </button>
      <aside className={`fixed inset-y-0 left-0 w-64 bg-[#343A40] text-white shadow-xl transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0 transition-transform z-30 flex flex-col`}>
        <div className="px-4 pt-5 pb-6 flex flex-col items-center border-b border-gray-700">
          <div className="w-full flex flex-col items-center">
            <Link to={homeTo} className="w-full mt-1 flex justify-center">
              {mainAppLogo ? (
                <img
                  src={mainAppLogo}
                  alt="App Logo"
                  className="object-contain"
                  style={{
                    width: '100%',
                    height: '4rem',
                    filter: 'brightness(0) invert(1) drop-shadow(0 1px 2px rgba(255,255,255,0.2))',
                  }}
                />
              ) : (
                <div className="w-full" style={{ height: '4rem' }} />
              )}
            </Link>
            <p
              className="w-full text-center font-medium text-gray-200"
              style={{
                marginTop: 3,
                fontSize: 12,
                letterSpacing: '0.14em',
                whiteSpace: 'nowrap',
                lineHeight: 1.3,
              }}
            >
              {appTagline}
            </p>
          </div>
        </div>

        <nav className="flex-grow overflow-y-auto p-2 space-y-2">
          {showDashboard && (
            <Link to="/dashboard" className={`w-full text-left px-4 py-3 flex items-center gap-4 rounded-lg transition-colors duration-200 ${location.pathname.endsWith('/dashboard') ? 'bg-[#c4151c] text-white font-semibold shadow-lg' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`}>
              <Icon path="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2z" />
              <span>Dashboard</span>
            </Link>
          )}
          {(visibleSales.length > 0 || visibleMagazine.length > 0 || visibleHr.length > 0 || visibleAttendance.length > 0) && (
            <div className="border-t border-gray-700 !my-4" />
          )}
          {visibleSales.length > 0 && (
            <DropdownMenu title="Sales & Billing" iconPath="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" links={visibleSales} defaultOpen={false} />
          )}
          {visibleMagazine.length > 0 && (
            <DropdownMenu
              title="Magazine Portal"
              iconPath="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              links={visibleMagazine}
              defaultOpen
              headerBadge={onOrdersPage ? 0 : newWebsiteOrders}
            />
          )}
          {visibleHr.length > 0 && (
            <DropdownMenu title="Human Resources" iconPath="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" links={visibleHr} />
          )}
                    {visibleAttendance.length > 0 && (
                        <DropdownMenu title="Attendance" iconPath="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" links={visibleAttendance} />
                    )}
                    {visibleEmailCenter.length > 0 && (
                        <>
                            <div className="border-t border-gray-700 !my-4" />
                            <DropdownMenu
                              title="Email Center"
                              iconPath="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                              links={visibleEmailCenter}
                              defaultOpen={location.pathname.includes('/admin/email')}
                            />
                        </>
                    )}
                    {isAdminUser(user) && (
                        <>
                            <div className="border-t border-gray-700 !my-4" />
                            <DropdownMenu title="Admin Area" iconPath="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924-1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" links={adminLinks} />
                        </>
                    )}
        </nav>
        <div className="p-4 border-t border-gray-700">
          {user && (
            <div className="text-center mb-4">
              <p className="text-sm font-semibold">{user.email || user.name || 'Admin'}</p>
              <p className="text-xs text-gray-400 capitalize">{user.role || 'admin'}</p>
            </div>
          )}
          <button onClick={handleLogout} className="w-full bg-gray-700 text-white py-2 px-4 rounded-lg shadow-md hover:bg-red-600 transition-colors">Logout</button>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
